import { format as formatDate } from "date-fns";
import { Calendar as CalendarIcon, Check, ChevronDown, History, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { Link, useNavigate } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  QueueStatCardsSection,
  QueueTeamPerformanceSection,
  UnderwritingQueueTable,
} from "@/components/UnderwritingDashboard";
import { underwritingService } from "@/services/UnderwritingService";
import { useAppData } from "@/context/AppDataContext";
import { pipelineService } from "@/services/PipelineService";
import { useApi } from "@/hooks/useApi";
import { UWQueueDashboardStats, UnderwritingActivity, QueuePeriod, QueueCustomRange } from "@/models/underwriting";
import { AgentPipelineModel, CustomerModel, ValCallModel, WIPModel } from "@/models/pipeline";
import { UnderwritingQueueClaim } from "@/services/UnderwritingService";
import { PRIME_PROPOSALS_AGENT_ID } from "@/constants/pipeline";
import { computeQueuePeriodRange } from "@/helpers/underwritingDashboard";
import { oldestQueueAgeMinutes } from "@/helpers/underwritingQueueDashboard";

const PERIOD_OPTIONS: { value: QueuePeriod; label: string }[] = [
  { value: "Today", label: "Today" },
  { value: "Last7Days", label: "Last 7 days" },
  { value: "Last30Days", label: "Last 30 days" },
  { value: "Custom", label: "Custom range" },
];

function formatUpdatedAgo(seconds: number): string {
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  return `${mins}m ago`;
}

// valCalls/wip have no age (daysOld/allocatedOn) of their own — pin them below the
// real customers/customersWarm regardless of the table's age-sort direction.
const NO_AGE_DAYS_OLD = -1;

function valCallToCustomer(valCall: ValCallModel): CustomerModel {
  return {
    applicationId: valCall.applicationId,
    customerName: valCall.customerName,
    agentName: null,
    allocatedOn: null,
    quotesTotalCount: 0,
    quoteStatus: null,
    lastTouch: null,
    handoverAllocatedUserId: valCall.handoverAllocatedUserId,
    handoverToUserId: valCall.handoverToUserId,
    customerLastOnlineTime: null,
    conversationsTotalCount: 0,
    conversationsOutboundCount: 0,
    conversationsInboundCount: 0,
    activities: [],
    daysOld: NO_AGE_DAYS_OLD,
    lastNote: valCall.lastNote ?? null,
  };
}

function wipToCustomer(wip: WIPModel): CustomerModel {
  return {
    applicationId: wip.applicationId,
    customerName: wip.customerName,
    agentName: wip.allocatedAgentName,
    allocatedOn: null,
    quotesTotalCount: 0,
    quoteStatus: null,
    lastTouch: null,
    handoverAllocatedUserId: wip.handoverAllocatedUserId,
    handoverToUserId: wip.handoverToUserId,
    customerLastOnlineTime: null,
    conversationsTotalCount: 0,
    conversationsOutboundCount: 0,
    conversationsInboundCount: 0,
    activities: [],
    daysOld: NO_AGE_DAYS_OLD,
    isPredictedToDoc: wip.isPredictedToPay,
    lastNote: wip.lastNote ?? null,
  };
}

export function UnderwritingQueue() {
  const navigate = useNavigate();
  const { currentUser } = useAppData();

  const [refreshKey, setRefreshKey] = useState(0);
  const [period, setPeriod] = useState<QueuePeriod>("Today");
  const [customRange, setCustomRange] = useState<QueueCustomRange | null>(null);
  const [periodPopoverOpen, setPeriodPopoverOpen] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [draftCustomRange, setDraftCustomRange] = useState<DateRange | undefined>(undefined);

  const { start: periodStart, end: periodEnd } = useMemo(
    () => computeQueuePeriodRange(period, customRange),
    [period, customRange]
  );

  const statsResult = useApi<UWQueueDashboardStats>(
    (signal) =>
      underwritingService.getQueueDashboardStats(
        `${periodStart}T00:00:00`,
        `${periodEnd}T23:59:59`,
        signal
      ),
    [periodStart, periodEnd, refreshKey]
  );

  const applicationsResult = useApi<AgentPipelineModel>(
    (signal) => pipelineService.getAgentPipeline(PRIME_PROPOSALS_AGENT_ID, signal),
    [refreshKey],
    { skip: !pipelineService.isReady() }
  );

  const applications = useMemo(() => {
    const data = applicationsResult.data;
    if (!data) return [];
    return [
      ...data.customers,
      ...data.customersWarm,
      ...data.valCalls.map(valCallToCustomer),
      ...data.wip.map(wipToCustomer),
    ];
  }, [applicationsResult.data]);

  const applicationIds = useMemo(() => applications.map((c) => c.applicationId), [applications]);

  // Batch claim-status check for every visible application, so each row can show
  // who holds it (and whether it's stale enough to claim over) in one round trip.
  const claimsResult = useApi<UnderwritingQueueClaim[]>(
    (signal) => underwritingService.getQueueClaimsByApplicationIds(applicationIds, signal),
    [applicationIds, refreshKey],
    { skip: applicationIds.length === 0 }
  );

  const claimsByApplicationId = useMemo(
    () => new Map((claimsResult.data ?? []).map((c) => [c.applicationId, c])),
    [claimsResult.data]
  );

  const stats = statsResult.data;

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const handleClaim = useCallback(
    async (customer: CustomerModel) => {
      if (!currentUser?.name) throw new Error("Current user is unavailable");
      try {
        // The claim/claim-over race and staleness check are handled atomically
        // server-side — a 409 here means it's genuinely held by someone else.
        const claim = await underwritingService.claimQueue(customer.applicationId, currentUser.userId, currentUser.name);
        const activity: UnderwritingActivity = {
          applicationId: customer.applicationId,
          resubmissionCount: 0,
          customerName: customer.customerName,
          affiliate: null,
          requestType: "Standard",
          channel: "",
          createdOn: customer.allocatedOn ?? new Date().toISOString(),
          claimedBy: claim.claimedBy,
          status: "Pending",
          latestDecision: null,
          agentName: customer.agentName,
        };
        navigate(`/dealer-direct-prop-queue/${customer.applicationId}`, {
          state: { activity, justClaimed: true, origin: { path: "/dealer-direct-prop-queue", label: "Underwriting Queue" } },
        });
      } catch (err) {
        handleRefresh();
        throw err;
      }
    },
    [navigate, currentUser, handleRefresh]
  );

  const handleRowClick = useCallback(
    (customer: CustomerModel) => {
      const activity: UnderwritingActivity = {
        applicationId: customer.applicationId,
        resubmissionCount: 0,
        customerName: customer.customerName,
        affiliate: null,
        requestType: "Standard",
        channel: "",
        createdOn: customer.allocatedOn ?? new Date().toISOString(),
        claimedBy: null,
        status: "Pending",
        latestDecision: null,
        agentName: customer.agentName,
      };
      navigate(`/dealer-direct-prop-queue/${customer.applicationId}`, {
        state: { activity, origin: { path: "/dealer-direct-prop-queue", label: "Dealer Direct Prop Queue" } },
      });
    },
    [navigate]
  );

  const isAnyLoading = statsResult.loading || applicationsResult.loading;

  // ─── Auto-refresh every 3 minutes + "Updated x ago" indicator ───────────────
  const AUTO_REFRESH_SECS = 180;
  const [lastUpdated, setLastUpdated] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setNow(Date.now());
      if ((Date.now() - lastUpdated) / 1000 >= AUTO_REFRESH_SECS) {
        handleRefresh();
        setLastUpdated(Date.now());
      }
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [handleRefresh, lastUpdated]);

  const handleManualRefresh = useCallback(() => {
    handleRefresh();
    setLastUpdated(Date.now());
    setNow(Date.now());
  }, [handleRefresh]);

  const updatedLabel = formatUpdatedAgo(Math.floor((now - lastUpdated) / 1000));
  const periodLabel = useMemo(() => {
    if (period !== "Custom" || !customRange) {
      return PERIOD_OPTIONS.find((option) => option.value === period)?.label ?? "Today";
    }
    const from = new Date(`${customRange.start}T00:00:00`);
    const to = new Date(`${customRange.end}T00:00:00`);
    return `${formatDate(from, "dd MMM")} - ${formatDate(to, "dd MMM")}`;
  }, [period, customRange]);

  const handlePeriodChange = (nextPeriod: QueuePeriod) => {
    if (nextPeriod === "Custom") {
      setDraftCustomRange(customRange ? {
        from: new Date(`${customRange.start}T00:00:00`),
        to: new Date(`${customRange.end}T00:00:00`),
      } : undefined);
      setShowCustomCalendar(true);
      return;
    }

    setPeriod(nextPeriod);
    setShowCustomCalendar(false);
    setPeriodPopoverOpen(false);
  };

  const handleCustomApply = () => {
    if (!draftCustomRange?.from || !draftCustomRange?.to) return;
    const from = draftCustomRange.from <= draftCustomRange.to ? draftCustomRange.from : draftCustomRange.to;
    const to = draftCustomRange.from <= draftCustomRange.to ? draftCustomRange.to : draftCustomRange.from;

    setCustomRange({ start: formatDate(from, "yyyy-MM-dd"), end: formatDate(to, "yyyy-MM-dd") });
    setPeriod("Custom");
    setShowCustomCalendar(false);
    setPeriodPopoverOpen(false);
  };

  // Sourced from the same pipeline list the Applications table renders, not the
  // stats API — so these two figures can never disagree with what's on screen.
  const inQueueCount = applications.length;
  const oldestInQueueMinutes = useMemo(
    () => oldestQueueAgeMinutes(applications, now),
    [applications, now]
  );

  return (
    <div className="flex h-full flex-col overflow-auto bg-zinc-100">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-6 py-3">
        <span className="text-sm font-semibold text-foreground">Dealer Direct Prop Queue</span>

        <div className="flex items-center gap-2">
          <Popover open={periodPopoverOpen} onOpenChange={setPeriodPopoverOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted">
                <CalendarIcon className="size-3.5 text-muted-foreground" />
                <span className="font-medium">{periodLabel}</span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              {!showCustomCalendar ? (
                <div className="w-40 p-1">
                  {PERIOD_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handlePeriodChange(option.value)}
                      className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                    >
                      <span className={period === option.value ? "font-semibold" : ""}>{option.label}</span>
                      {period === option.value && <Check className="size-4" />}
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <div className="border-b px-3 py-2 text-sm font-medium text-foreground">Select date range</div>
                  <Calendar
                    mode="range"
                    selected={draftCustomRange}
                    onSelect={setDraftCustomRange}
                    numberOfMonths={1}
                    captionLayout="dropdown"
                  />
                  <div className="flex items-center justify-end gap-2 border-t p-3">
                    <button
                      className="rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
                      onClick={() => {
                        setShowCustomCalendar(false);
                        setPeriodPopoverOpen(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
                      disabled={!draftCustomRange?.from || !draftCustomRange?.to}
                      onClick={handleCustomApply}
                    >
                      Apply
                    </button>
                  </div>
                </>
              )}
            </PopoverContent>
          </Popover>

          {/* Refresh */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleManualRefresh}
              disabled={isAnyLoading}
              className="flex items-center justify-center rounded-md border border-border bg-card p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
              aria-label="Refresh"
            >
              <RefreshCw className={`size-4 ${isAnyLoading ? "animate-spin" : ""}`} />
            </button>
            <span className="text-[11px] leading-tight text-muted-foreground">
              Updated
              <br />
              <span className="font-medium text-emerald-600">{updatedLabel}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Error banners */}
      {(statsResult.error || applicationsResult.error) && (
        <div className="mx-6 mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {statsResult.error ?? applicationsResult.error}
        </div>
      )}

      {/* Content */}
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 px-6 py-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-[22px] font-semibold text-foreground">Dealer Direct Prop Queue</h1>
          <Link
            to="/dealer-direct-prop-queue/audit-log"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:bg-muted"
          >
            <History className="size-3.5" />
            Audit log
          </Link>
        </div>
        {/* Stat cards */}
        {statsResult.loading && !stats ? (
          <div className="flex h-28 items-center justify-center">
            <Spinner />
          </div>
        ) : stats ? (
          <QueueStatCardsSection
            stats={stats}
            inQueueCount={inQueueCount}
            oldestInQueueMinutes={oldestInQueueMinutes}
          />
        ) : null}

        {/* Team Performance */}
        <QueueTeamPerformanceSection start={periodStart} end={periodEnd} refreshKey={refreshKey} />
      </div>

      {/* Application queue — full-width white band, content constrained to the 1200px column */}
      <div className="w-full bg-white">
        <div className="mx-auto w-full max-w-[1200px] px-6 py-5">
          <UnderwritingQueueTable
            customers={applications}
            claimsByApplicationId={claimsByApplicationId}
            currentUserName={currentUser?.name ?? null}
            isLoading={applicationsResult.loading}
            onRowClick={handleRowClick}
            onClaim={handleClaim}
          />
        </div>
      </div>
    </div>
  );
}
